import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/app/hooks";
import { lazy, Suspense } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { wholeWorkflowAPI } from "src/store/features/dashboard/wholeWorkflowSlice";
import { userChoiceHistoryWorkflowAPI } from "src/store/features/dashboard/userChoiceHistoryWorkflowSlice";
import WorkspaceStatusModal from "src/components/common/modal/WorkspaceStatusModal";
import { muiInput, RoundLibraryLists, RoundStatuses, ToolGroupConfig, TOOL_GROUPS, UsageData, WorkflowSummary } from "src/components/dashboard/dashboardTypes";
import { CollapsibleSection, SectionLabel, StatCard, ToolGroupCard } from "src/components/dashboard/dashboardComponents";
import LanguageToggle from "src/components/common/LanguageToggle";
import { setLocale } from "src/store/features/settingSlice";
import CapsyCharacter from "src/components/CapsyCharacter";

// recharts는 무거워 초기 번들에서 분리(lazy). 대시보드 첫 진입 속도 개선.
const WorkflowUsageBarChart = lazy(() =>
  import("src/pages/DashboardCharts").then((m) => ({
    default: m.WorkflowUsageBarChart,
  }))
);
const UsageTrendAreaChart = lazy(() =>
  import("src/pages/DashboardCharts").then((m) => ({
    default: m.UsageTrendAreaChart,
  }))
);

// 브랜드 색상. 워크플로우 탭 헤더(#4968A2)를 기준으로 톤 변형을 맞춘다.
// 대시보드만 indigo/violet 계열을 쓰던 것을 앱 전체 네이비 계열로 통일하기 위한 상수.
const BRAND = "#4968A2";
const BRAND_DEEP = "#3a5282";

// 대시보드 페이지 레이아웃 스타일 정의
const PAGE_STYLE: React.CSSProperties = {
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  // 배경은 브랜드 네이비를 옅게 깐 톤. 흰색으로 날아가지 않게 양끝에 색을 남긴다.
  background: "linear-gradient(160deg, #e9eff8 0%, #f7f9fc 45%, #dfe8f4 100%)",
  minHeight: "100vh",
  padding: "56px 60px 96px",
  letterSpacing: "-0.01em",
};

// 워크플로우 상태 차트 색상 정의
const STATUS_CHART_CONFIG = {
  completed: { color: "#4f8f9f", labelKey: "dashboard.completed" },
  running: { color: "#8aa6c1", labelKey: "dashboard.running" },
  failed: { color: "#c98282", labelKey: "dashboard.failed" },
  notStarted: { color: "#d6dde6", labelKey: "dashboard.notStarted" },
};

// 로그인 후 대시보드 페이지 정의
const DashboardPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [, setCookie] = useCookies(["drugvlab-settings"]);

  const email = useAppSelector((s) => s.userInfo?.response?.email);
  const userName = email ? email.slice(0, email.indexOf("@")) : "";
  // 사이드바가 실제로 렌더되는 폭. 본문 오프셋을 여기에 맞춘다.
  const sidebarWidth = useAppSelector((s) => s.drawer?.drawerWidth ?? 240);
  const dashboardWholeWorkflow = useAppSelector((s) => s?.dashboardHistory?.response);
  const isWorkflowUsageLoading = useAppSelector((s) => s?.dashboardHistory?.isLoading);
  const userChoiceHistoryWorkflow = useAppSelector((s) => s?.userChoiceHistoryWorkflow?.response);

  const [overviewSelectedWorkflowType, setOverviewSelectedWorkflowType] = useState("PPI Inhibitor");
  const [selectedRoundType, setSelectedRoundType] = useState("round_1");
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<"COMPLETED" | "RUNNING" | "FAILED" | "not_started" | null>(null);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);

  const [selectedWorkflowType, setSelectedWorkflowType] = useState("PPI Inhibitor");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [usageData, setUsageData] = useState<UsageData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const currentLang = i18n.language?.startsWith("ko") || i18n.language?.startsWith("kr")
      ? "ko"
      : "en";
  const workflowOptionLabel = React.useCallback((workflowType: string) =>
    t(`dashboard.workflowOptions.${workflowType}`, {
      defaultValue: workflowType,
    }), [t]);

  const normalizeWorkflowLabel = React.useCallback((workflowKey: string) => {
    const normalized = workflowKey.toLowerCase().replace(/[\s_-]/g, "");
    if (normalized.includes("ppi")) return "PPI Inhibitor";
    if (normalized.includes("kinase")) return "Kinase Inhibitor";
    if (normalized.includes("biomarker") || normalized.includes("chemtarget")) return "Biomarker";
    if (normalized.includes("drugresponse")) return "Drug Response";
    if (normalized.includes("toxicity") || normalized.includes("drugtoxicity")) return "Drug Toxicity";
    // if (normalized.includes("mdtr")) return "MDTR";
    return workflowKey;
  }, []);

  // 워크플로우 키를 다양한 변형으로 확인하여 올바른 키를 반환하는 함수 정의
  const resolveWorkflowKey = React.useCallback(
    (obj: Record<string, any> | undefined, workflowLabel: string): string | null => {
      if (!obj) return null;
      const candidates = [
        workflowLabel,
        workflowLabel.replace(/\s+/g, "_"),
        workflowLabel.replace(/\s+/g, "").toLowerCase(),
        workflowLabel.toLowerCase().replace(/\s+/g, "_"),
        workflowLabel.toLowerCase().replace(/\s+/g, ""),
      ];
      for (const key of candidates) {
        if (key in obj) return key;
      }
      return null;
    },
    []
  );

  // 워크플로우 사용 현황 데이터 가져오기 및 업데이트
  useEffect(() => {
    dispatch(
      userChoiceHistoryWorkflowAPI({
        group_name: "temp_group",
        start_date: startDate,
        end_date: endDate,
        round: 1,
        workflow_name: selectedWorkflowType,
      })
    );
  }, [startDate, endDate, selectedWorkflowType, dispatch]);

  // 전체 워크플로우 데이터 가져오기
  useEffect(() => {
    updateUsageData();
  }, [userChoiceHistoryWorkflow, selectedWorkflowType, i18n.language]);

  useEffect(() => {
    dispatch(wholeWorkflowAPI());
  }, [dispatch]);

  // 대시보드 데이터 가공 함수 정의
  const getRoundStatuses = (wf: string, round: string): RoundStatuses => {
    const statuses = dashboardWholeWorkflow?.round_statuses as Record<string, any> | undefined;
    const resolvedKey = resolveWorkflowKey(statuses, wf);
    return (resolvedKey ? statuses?.[resolvedKey]?.[round] : undefined) || {
      COMPLETED: 0,
      FAILED: 0,
      not_started: 0,
      RUNNING: 0,
    };
  };

  // 워크플로우별 라운드 라이브러리 목록 가져오기
  const getRoundLibraryLists = (wf: string, round: string): RoundLibraryLists => {
    const lists = dashboardWholeWorkflow?.round_library_lists as Record<string, any> | undefined;
    const resolvedKey = resolveWorkflowKey(lists, wf);
    return (resolvedKey ? lists?.[resolvedKey]?.[round] : undefined) || {
      COMPLETED: [],
      FAILED: [],
      not_started: [],
      RUNNING: [],
    };
  };

  // 현재 선택된 라운드의 상태 데이터 가져오기
  const getCurrentRoundData = (): RoundStatuses =>
    getRoundStatuses(overviewSelectedWorkflowType, selectedRoundType);

  // 워크플로우 사용 현황 차트 데이터 가공
  const workflowUsageChartData = React.useMemo(() => {
    const statuses = dashboardWholeWorkflow?.round_statuses as Record<string, Record<string, RoundStatuses>> | undefined;
    if (!statuses) return [];

    const grouped = new Map<
      string,
      {
        name: string;
        workflow: string;
        round: string;
        completed: number;
        running: number;
        failed: number;
        notStarted: number;
        total: number;
      }
    >();

    // 워크플로우별 라운드 상태 데이터를 그룹화하여 차트 데이터 생성
    Object.entries(statuses).forEach(([workflowKey, rounds]) => {
      const workflow = normalizeWorkflowLabel(workflowKey);
      Object.entries(rounds || {}).forEach(([roundKey, counts]) => {
        const mapKey = `${workflow}-${roundKey}`;
        const current =
          grouped.get(mapKey) || {
            name: `${workflowOptionLabel(workflow)} ${t("dashboard.roundLabel", {
              round: roundKey.replace("round_", ""),
            })}`,
            workflow,
            round: roundKey,
            completed: 0,
            running: 0,
            failed: 0,
            notStarted: 0,
            total: 0,
          };

        current.completed += counts?.COMPLETED || 0;
        current.running += counts?.RUNNING || 0;
        current.failed += counts?.FAILED || 0;
        current.notStarted += counts?.not_started || 0;
        current.total =
          current.completed + current.running + current.failed + current.notStarted;
        grouped.set(mapKey, current);
      });
    });

    // 워크플로우 순서 정의 및 정렬
    const workflowOrder = ["Biomarker", "PPI Inhibitor", "Kinase Inhibitor", "Drug Response", "Drug Toxicity"];
    return Array.from(grouped.values()).sort((a, b) => {
      const workflowDiff = workflowOrder.indexOf(a.workflow) - workflowOrder.indexOf(b.workflow);
      if (workflowDiff !== 0) return workflowDiff;
      return Number(a.round.replace("round_", "")) - Number(b.round.replace("round_", ""));
    });
  }, [dashboardWholeWorkflow, normalizeWorkflowLabel, workflowOptionLabel, t]);

  // 워크플로우 사용 현황 총합 계산
  const workflowUsageTotals = React.useMemo(
    () =>
      workflowUsageChartData.reduce(
        (acc, item) => ({
          completed: acc.completed + item.completed,
          running: acc.running + item.running,
          failed: acc.failed + item.failed,
          notStarted: acc.notStarted + item.notStarted,
          total: acc.total + item.total,
        }),
        { completed: 0, running: 0, failed: 0, notStarted: 0, total: 0 }
      ),
    [workflowUsageChartData]
  );

  // 현재 선택된 워크플로우의 사용 현황 데이터 가져오기
  const getAvailableRounds = (): string[] => {
    const lists = dashboardWholeWorkflow?.round_library_lists as Record<string, any> | undefined;
    const resolvedKey = resolveWorkflowKey(lists, overviewSelectedWorkflowType);
    const rl = resolvedKey ? lists?.[resolvedKey] : undefined;
    if (!rl) return ["round_1"];
    return Object.keys(rl).filter((k) => k.startsWith("round_")).sort();
  };

  // 현재 선택된 라운드의 라이브러리 수 계산
  const getFilteredLibraryCount = (): number => {
    const l = getRoundLibraryLists(overviewSelectedWorkflowType, selectedRoundType);
    return (
      (l?.COMPLETED?.length || 0) +
      (l?.FAILED?.length || 0) +
      (l?.not_started?.length || 0) +
      (l?.RUNNING?.length || 0)
    );
  };

  // 워크플로우 사용 현황 데이터 가져오기
  const getCurrentWorkflowData = (): WorkflowSummary[] => {
    const summaries = userChoiceHistoryWorkflow?.workflow_summaries as
      | Record<string, { summary?: WorkflowSummary[] }>
      | undefined;
    if (!summaries) return [];

    const workflowCandidates: Record<string, string[]> = {
      "Biomarker": ["biomarker", "Biomarker", "chemtarget"],
      "PPI Inhibitor": ["PPI_Inhibitor", "ppi_inhibitor"],
      "Kinase Inhibitor": ["Kinase_Inhibitor", "kinase_inhibitor"],
      "Drug Response": ["drug_response", "Drug_Response"],
      "Drug Toxicity": ["toxicity", "drug_toxicity"],
      // "MDTR": ["mdtr", "MDTR"],
    };

    const candidates = workflowCandidates[selectedWorkflowType] || [selectedWorkflowType];
    const merged = candidates?.flatMap((key) => summaries?.[key]?.summary || []);
    return merged;
  };

  // 워크플로우 사용 현황 데이터 업데이트
  const updateUsageData = () => {
    setIsLoading(true);
    try {
      const summaries = getCurrentWorkflowData();
      if (!summaries?.length) { setUsageData([]); return; }
      setUsageData(
        summaries
          .map((s) => ({
            period: s.period,
            count: s.count || 0,
            displayLabel: new Date(s.period).toLocaleDateString(
              i18n.language?.startsWith("ko") ? "ko-KR" : "en-US",
              { month: "short", day: "numeric" }
            ),
          }))
          .sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime())
      );
    } catch {
      setUsageData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // 워크플로우 상태 카드 클릭 시 모달 표시
  const handleStatusCardClick = (
    status: "COMPLETED" | "RUNNING" | "FAILED" | "not_started"
  ) => {
    const l = getRoundLibraryLists(overviewSelectedWorkflowType, selectedRoundType);
    setSelectedStatus(status);
    setSelectedWorkspaces(l[status] || []);
    setShowStatusModal(true);
  };

  // 모달 닫기 핸들러
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setStartDate(v);
    if (v > endDate) setEndDate(v);
  };

  // 종료 날짜 변경 핸들러
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setEndDate(v);
    if (v < startDate) setStartDate(v);
  };

  // 워크플로우 선택 시 상태 초기화
  const handleSelectToolGroup = (group: ToolGroupConfig) => {
    navigate("/dashboard/detail", { state: { group } });
  };

  // 워크플로우 사용 현황 새로고침 핸들러
  const handleRefreshWorkflowUsage = () => {
    dispatch(wholeWorkflowAPI());
  };

  // 워크플로우 상태 카드 데이터 정의
  const statCards = [
    {
      label: t("dashboard.allProjects"),
      value: getFilteredLibraryCount(),
      color: BRAND,
      bg: "rgba(73,104,162,0.08)",
      border: "rgba(73,104,162,0.2)",
      status: null,
    },
    {
      label: t("dashboard.completed"),
      value: getCurrentRoundData()?.COMPLETED || 0,
      color: "#10b981",
      bg: "rgba(16,185,129,0.08)",
      border: "rgba(16,185,129,0.2)",
      status: "COMPLETED",
    },
    {
      label: t("dashboard.running"),
      value: getCurrentRoundData()?.RUNNING || 0,
      color: "#f59e0b",
      bg: "rgba(245,158,11,0.08)",
      border: "rgba(245,158,11,0.2)",
      status: "RUNNING",
    },
    {
      label: t("dashboard.failed"),
      value: getCurrentRoundData()?.FAILED || 0,
      color: "#ef4444",
      bg: "rgba(239,68,68,0.08)",
      border: "rgba(239,68,68,0.2)",
      status: "FAILED",
    },
    {
      label: t("dashboard.notStarted"),
      value: getCurrentRoundData()?.not_started || 0,
      color: "#94a3b8",
      bg: "rgba(148,163,184,0.08)",
      border: "rgba(148,163,184,0.2)",
      status: "not_started",
    },
  ];
  
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////// 페이지 렌더링 ////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  return (
    <>
      <div style={{
        ...PAGE_STYLE,
        // 사이드바는 Redux drawerWidth 로 그려지므로 같은 값을 써야 틈이 생기지 않는다.
        // CSS 변수 --drawer-width 는 expandedWidth(248) 를 담아 실제 폭(240)과 8px 어긋났다.
        marginLeft: `${sidebarWidth}px`,
        transition: "margin-left 0.3s ease",
      }}>
        <div style={{ maxWidth: "100%", margin: "0 auto" }}>
          {/* 헤더 영역 정의 */}
          <div style={{ marginBottom: 56 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, marginBottom: 22 }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  background: "rgba(73,104,162,0.09)",
                  border: "1px solid rgba(73,104,162,0.2)",
                  borderRadius: 100,
                  padding: "5px 16px",
                }}
              >
                <span
                  style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: BRAND, display: "inline-block",
                    boxShadow: "0 0 0 3px rgba(73,104,162,0.25)",
                  }}
                />
                <span
                  style={{
                    fontSize: 11, fontWeight: 700, color: BRAND,
                    letterSpacing: "0.07em", textTransform: "uppercase",
                  }}
                >
                  {t("dashboard.platform")}
                </span>
              </div>
              <LanguageToggle
                currentLang={currentLang}
                onChange={(newLang) => {
                  const nextLocale = newLang === "ko" ? "kr" : "en";
                  setCookie("drugvlab-settings", { locale: nextLocale }, { path: "/" });
                  dispatch(setLocale(nextLocale));
                  i18n.changeLanguage(nextLocale).catch(console.error);
                }}
              />
            </div>
            <h1
              style={{
                fontSize: 38, fontWeight: 800, color: "#0f172a",
                margin: "0 0 14px", letterSpacing: "-0.04em", lineHeight: 1.15,
                display: "flex", alignItems: "center", gap: 0,
              }}
            >
              <span>
                {t("dashboard.greetingPrefix")}{" "}
                <span style={{ color: BRAND }}>
                  {userName}
                </span>
              </span>{" "}
              <span
                style={{ width: 120, height: 120, flexShrink: 0, margin: "-28px 0 -24px -14px" }}
                aria-hidden="true"
              >
                <CapsyCharacter modelScale={2} />
              </span>
            </h1>
            <div style={{ display: "flex", gap: 14, maxWidth: 760 }}>
              <span
                style={{
                  width: 3,
                  flexShrink: 0,
                  borderRadius: 999,
                  background: `linear-gradient(180deg, ${BRAND} 0%, ${BRAND_DEEP} 100%)`,
                }}
                aria-hidden="true"
              />
              <div>
                <p style={{ fontSize: 17, fontWeight: 650, color: "#334155", margin: "0 0 4px", lineHeight: 1.55 }}>
                  {t("dashboard.heroDescPrimary")}
                </p>
                <p style={{ fontSize: 15, color: "#64748b", margin: 0, lineHeight: 1.65 }}>
                  {t("dashboard.heroDescSecondary")}
                </p>
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                  <span
                    style={{
                      padding: "3px 9px",
                      border: "1px solid rgba(99, 102, 241, 0.2)",
                      borderRadius: 999,
                      background: "rgba(99, 102, 241, 0.08)",
                      color: "#4f46e5",
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {t("dashboard.capsyAiLabel")}
                  </span>
                  <span style={{ color: "#64748b", fontSize: 14, lineHeight: 1.6 }}>
                    {t("dashboard.capsyAiDescription")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 도구 카드 영역 정의 */}
          <div style={{ marginBottom: 40 }}>
            <SectionLabel>{t("dashboard.targetDiscoverySection")}</SectionLabel>
            <div style={{ display: "grid", marginBottom:25,gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 28, width: "100%" }}>
              {TOOL_GROUPS.filter((group) => group.id === "biomarkerDiscovery").map((group) => (
                <ToolGroupCard
                  key={group.id}
                  group={group}
                  onSelect={handleSelectToolGroup}
                />
              ))}
            </div>
            <SectionLabel>{t("dashboard.postTargetDiscoverySection")}</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 28, width: "100%" }}>
              {TOOL_GROUPS.filter((group) => group.id !== "biomarkerDiscovery").map((group) => (
                <ToolGroupCard
                  key={group.id}
                  group={group}
                  onSelect={handleSelectToolGroup}
                />
              ))}
            </div>
          </div>

          

          {/* 워크스페이스 현황 영역 정의 */}
          <CollapsibleSection
            title={t("dashboard.overviewTitle")}
            subtitle={t("dashboard.overviewSubtitle")}
            defaultOpen={false}
            accentColor={BRAND}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "12px", mb: "24px", flexWrap: "wrap" }}>
              <FormControl size="small" sx={muiInput} style={{ minWidth: 190 }}>
                <InputLabel id="ov-tool">{t("dashboard.toolName")}</InputLabel>
                <Select
                  labelId="ov-tool"
                  value={overviewSelectedWorkflowType}
                  label={t("dashboard.toolName")}
                  onChange={(e: SelectChangeEvent<string>) => {
                    setOverviewSelectedWorkflowType(e.target.value);
                    setSelectedRoundType("round_1");
                  }}
                >
                  <MenuItem value="Biomarker">{workflowOptionLabel("Biomarker")}</MenuItem>
                  <MenuItem value="PPI Inhibitor">{workflowOptionLabel("PPI Inhibitor")}</MenuItem>
                  <MenuItem value="Kinase Inhibitor">{workflowOptionLabel("Kinase Inhibitor")}</MenuItem>
                  <MenuItem value="Drug Response">{workflowOptionLabel("Drug Response")}</MenuItem>
                  <MenuItem value="Drug Toxicity">{workflowOptionLabel("Drug Toxicity")}</MenuItem>
                  {/* <MenuItem value="MDTR">{workflowOptionLabel("MDTR")}</MenuItem> */}
                </Select>
              </FormControl>
              <FormControl size="small" sx={muiInput} style={{ minWidth: 150 }}>
                <InputLabel id="ov-round">{t("dashboard.round")}</InputLabel>
                <Select
                  labelId="ov-round"
                  value={selectedRoundType}
                  label={t("dashboard.round")}
                  onChange={(e: SelectChangeEvent<string>) => setSelectedRoundType(e.target.value)}
                >
                  {getAvailableRounds().map((r) => (
                    <MenuItem key={r} value={r}>
                      {t("dashboard.roundLabel", { round: r.replace("round_", "") })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: "14px" }}>
              {statCards?.map((sc) => (
                <StatCard
                  key={sc.label}
                  sc={sc}
                  onClick={sc.status ? () => handleStatusCardClick(sc.status as any) : undefined}
                />
              ))}
            </div>
          </CollapsibleSection>

          {/* 워크플로우 사용 현황 영역 정의 */}
          <CollapsibleSection
            title={t("dashboard.workflowUsageTitle")}
            subtitle={t("dashboard.workflowUsageSubtitle")}
            defaultOpen={true}
            accentColor="#4f8f9f"
          >
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 18 }}>
              <button
                type="button"
                onClick={handleRefreshWorkflowUsage}
                disabled={isWorkflowUsageLoading}
                title={t("dashboard.refresh", { defaultValue: "Refresh" })}
                aria-label={t("dashboard.refresh", { defaultValue: "Refresh" })}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  height: 36,
                  padding: "0 14px",
                  borderRadius: 10,
                  border: "1px solid #dbe5ed",
                  background: isWorkflowUsageLoading ? "#f1f5f9" : "#ffffff",
                  color: isWorkflowUsageLoading ? "#94a3b8" : "#4f8f9f",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: isWorkflowUsageLoading ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
                  transition: "background 0.15s ease, border-color 0.15s ease, color 0.15s ease",
                }}
              >
                <RefreshCw size={15} strokeWidth={2.2} />
                {isWorkflowUsageLoading
                  ? t("dashboard.loading")
                  : t("dashboard.refresh", { defaultValue: "Refresh" })}
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 20, alignItems: "stretch" }}>
              <div
                style={{
                  background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
                  border: "1px solid #edf0f7",
                  borderRadius: 16,
                  padding: "22px 20px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 18,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
                    {t("dashboard.totalLibraries")}
                  </div>
                  <div style={{ fontSize: 42, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
                    {dashboardWholeWorkflow?.library_count || 0}
                  </div>
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  {Object.entries(STATUS_CHART_CONFIG).map(([key, config]) => (
                    <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: config.color }} />
                        {t(config.labelKey)}
                      </span>
                      <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 800 }}>
                        {workflowUsageTotals[key as keyof typeof workflowUsageTotals]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: "16px",
                  padding: "24px 20px 14px",
                  border: "1px solid #edf0f7",
                  minWidth: 0,
                }}
              >
                {workflowUsageChartData.length ? (
                  <Suspense
                    fallback={
                      <div style={{ height: 330, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 14 }}>
                        …
                      </div>
                    }
                  >
                    <WorkflowUsageBarChart
                      data={workflowUsageChartData}
                      statusConfig={STATUS_CHART_CONFIG}
                    />
                  </Suspense>
                ) : (
                  <div style={{ height: 330, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 14 }}>
                    {t("dashboard.noWorkflowUsageData")}
                  </div>
                )}
              </div>
            </div>
          </CollapsibleSection>

          {/* 사용 추이 영역 정의 */}
          <CollapsibleSection
            title={t("dashboard.usageTrendTitle")}
            defaultOpen={false}
            accentColor="#10b981"
          >
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
              <TextField
                label={t("dashboard.startDay")}
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                InputLabelProps={{ shrink: true }}
                sx={muiInput}
                size="small"
              />
              <TextField
                label={t("dashboard.endDay")}
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                InputLabelProps={{ shrink: true }}
                sx={muiInput}
                size="small"
                inputProps={{ min: startDate }}
              />
              <FormControl size="small" sx={muiInput} style={{ minWidth: 190 }}>
                <InputLabel id="ut-wf">{t("dashboard.workflowType")}</InputLabel>
                <Select
                  labelId="ut-wf"
                  value={selectedWorkflowType}
                  label={t("dashboard.workflowType")}
                  onChange={(e: SelectChangeEvent<string>) => setSelectedWorkflowType(e.target.value)}
                >
                  <MenuItem value="Biomarker">{workflowOptionLabel("Biomarker")}</MenuItem>
                  <MenuItem value="PPI Inhibitor">{workflowOptionLabel("PPI Inhibitor")}</MenuItem>
                  <MenuItem value="Kinase Inhibitor">{workflowOptionLabel("Kinase Inhibitor")}</MenuItem>
                  <MenuItem value="Drug Response">{workflowOptionLabel("Drug Response")}</MenuItem>
                  <MenuItem value="Drug Toxicity">{workflowOptionLabel("Drug Toxicity")}</MenuItem>
                  {/* <MenuItem value="MDTR">{workflowOptionLabel("MDTR")}</MenuItem> */}
                </Select>
              </FormControl>
            </div>
            <div
              style={{
                position: "relative",
                background: "#f8fafc",
                borderRadius: "14px",
                padding: "24px 20px 16px",
                border: "1px solid #edf0f7",
              }}
            >
              {isLoading && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(248,250,252,0.9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "14px",
                    fontSize: "14px",
                    color: "#64748b",
                  }}
                >
                  {t("dashboard.loading")}
                </div>
              )}
              <Suspense
                fallback={
                  <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 14 }}>
                    …
                  </div>
                }
              >
                <UsageTrendAreaChart data={usageData} brand={BRAND} />
              </Suspense>
            </div>
          </CollapsibleSection>
        </div>
      </div>

      <WorkspaceStatusModal
        open={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setSelectedStatus(null);
          setSelectedWorkspaces([]);
        }}
        status={selectedStatus}
        workspaces={selectedWorkspaces}
        workflowType={overviewSelectedWorkflowType}
        roundType={selectedRoundType}
      />
    </>
  );
};

export default DashboardPage;
